import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

import AppDashboard from "@/pages/AppDashboard";
import LoginPage from "@/pages/LoginPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import BillingPage from "@/pages/BillingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BlockedPage from "@/pages/BlockedPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import ProfilePage from "@/pages/ProfilePage";
import SignupPage from "@/pages/SignupPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResendVerificationPage from "@/pages/ResendVerificationPage";
import LandingPage from "@/pages/LandingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import CookieConsent from "@/components/CookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGIN_BOOTSTRAP_KEY = "bubble-radar-login-bootstrap";

function readLoginBootstrap() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(LOGIN_BOOTSTRAP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const createdAt = Number(parsed?.createdAt || 0);
    if (!createdAt || (Date.now() - createdAt) > 15000) {
      window.sessionStorage.removeItem(LOGIN_BOOTSTRAP_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearLoginBootstrap() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(LOGIN_BOOTSTRAP_KEY);
}

async function fetchMeWithRetry(maxAttempts = 3) {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const resp = await axios.get(`${API}/me`, { withCredentials: true });
      if (resp?.data?.id) {
        clearLoginBootstrap();
        return resp.data;
      }
    } catch (e) {
      if (attempt === maxAttempts) {
        return null;
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
  }

  return null;
}

function RequireAuth({ children }) {
  const loc = useLocation();
  const [state, setState] = useState({ loading: true, me: null });

  useEffect(() => {
    let active = true;
    (async () => {
      const bootstrap = readLoginBootstrap();
      const me = await fetchMeWithRetry(bootstrap ? 8 : 3);
      if (!active) {
        return;
      }
      setState({ loading: false, me });
    })();

    return () => {
      active = false;
    };
  }, []);

  if (state.loading) return null;

  if (!state.me) {
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }

  return children;
}

function RequireAccess({ children }) {
  const loc = useLocation();
  const [state, setState] = useState({ loading: true, me: null });

  useEffect(() => {
    let active = true;
    (async () => {
      const bootstrap = readLoginBootstrap();
      const me = await fetchMeWithRetry(bootstrap ? 8 : 3);
      if (!active) {
        return;
      }
      setState({ loading: false, me });
    })();

    return () => {
      active = false;
    };
  }, []);

  if (state.loading) return null;
  if (!state.me?.id) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;

  if (!state.me?.hasAccess) return <Navigate to="/blocked" replace />;

  return children;
}

export default function AppRouter() {
  // Global interceptor: if backend returns 402, redirect to /blocked.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const code = err?.response?.status;
        if (code === 402) {
          window.location.href = "/blocked";
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <CookieConsent />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/resend-verification" element={<ResendVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/billing" element={<RequireAuth><BillingPage /></RequireAuth>} />
        <Route path="/billing/success" element={<RequireAuth><BillingSuccessPage /></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
        <Route path="/blocked" element={<RequireAuth><BlockedPage /></RequireAuth>} />
        <Route path="/admin/users" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />

        <Route path="/app" element={<RequireAccess><AppDashboard /></RequireAccess>} />

        {/* Default */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
